import type { FAQs } from '@/types/referAndEarn';

export const lutsFaqContent: FAQs = [
  {
    question: 'What is a LUT?',
    answer:
      'A LUT (short for lookup table) is a color preset that changes the look of your video by remapping its colors. You can use LUTs to quickly create a cinematic style, keep colors consistent across different camera footage, or speed up your color grading workflow – just apply the LUT in your editing software and tweak from there if needed.',
  },
  {
    question: 'How do I use LUTs?',
    answer: `After you download a LUT from Uppbeat, it'll arrive as a .zip file. Extract it, then open the Assets folder and you'll usually find a .cube LUT file inside.

From there, apply it in your editing software:

- **Premiere Pro:** Open Lumetri Color → Basic Correction → Input LUT → Browse → select the LUT file.
- **After Effects:** Go to File → Apply Color LUT… → select the LUT file from the Assets folder.
- **DaVinci Resolve:** Go to the Color page → in the LUTs panel right-click Import LUT → choose the .cube file → then drag the LUT onto a node to apply it.`,
  },
  {
    question: 'Can I use LUTs with any footage?',
    answer: `Yes – you can apply LUTs to pretty much any video footage, but the results will vary depending on how your clip was shot. LUTs are designed to apply a color grade or look, and they're often used to create a consistent style across different shots or cameras.

For the best-looking results, we recommend:

- Starting with well-exposed footage with a neutral white balance.
- If your footage is shot in a Log/flat profile, doing a basic correction or Log-to-Rec.709 conversion first, then applying your creative LUT.
- Making small tweaks after (exposure, contrast, saturation) to fine-tune the look.`,
  },
  {
    question: "What's the difference between LUTs and Color Grading Presets?",
    answer: `LUTs are files (usually .cube) that apply a specific color "look" by remapping the colors in your footage. They're a quick way to add a cinematic grade, unify shots, or speed up your workflow.

Color grading presets (like Lumetri presets in Premiere Pro) are saved adjustment settings inside a specific piece of software – things like exposure, contrast, curves, saturation, and more.

In simple terms:

- LUT = a color map file you can load into lots of editors.
- Preset = a bundle of grading settings that's usually software-specific and easier to tweak setting-by-setting after you apply it.`,
  },
  {
    question: 'Can I use Uppbeat LUTs in commercial videos?',
    answer: `It depends on what you mean by "commercial".

- Yes – LUTs from Uppbeat are fine to use in monetized online content (like YouTube, social, sponsored videos, etc.) as long as you've downloaded them on an Uppbeat account and you're following our usage policy.
- For paid advertising or client work, you'll need the extended license: [Uppbeat Pro](/pricing) if you're a freelancer or solo professional, or [Uppbeat Business](/pricing) if you're a company, brand, or agency.`,
  },
  {
    question: 'Which software is compatible with Uppbeat LUTs?',
    answer: `Our LUTs usually download as .cube files, so they'll work in any editing or grading software that supports importing .cube LUTs.

We provide setup instructions for these popular options:

- Adobe Premiere Pro (via the Lumetri Color panel)
- Adobe After Effects (Apply Color LUT…)
- DaVinci Resolve (Import LUT, then apply to a node)

You can double-check an asset's details by hovering over its preview or opening the asset page before downloading. LUTs labeled "Drag and Drop" are compatible with any software that supports .cube files.`,
  },
];
